import { useParams, useNavigate } from "@tanstack/react-router"
import { Page } from "../../../../-components/Page"
import { ColumnLayout } from "../../-components/column-layout"
import { DetailsCard } from "../../-components/details-card"
import { LabeledValue } from "../../../../-components/default-list/labeled-value"
import { Text, Box, useMantineColorScheme } from "@mantine/core"
import { FieldData } from "../../../../-components/field-data"
import { useTranslation } from "react-i18next"
import { AttachmentCarousel } from "../../-components/AttachmentCarousel"
import {
  useGetCondition,
  useGetResourceFieldTypes,
  useGetResourceSuspense,
} from "@/orval/gen"
import { useGetResourceObjectsByResourceIdSuspense } from "@/orval/gen"
import { useGetConditionsByResourceId } from "@/orval/gen"
import { getFormattedCurrency } from "@/utils/get-formatted-number"
import { getFileType } from "@/utils/getFileType"
import { CreateResourceObjectsActionIcon } from "./CreateResourceObjectsActionIcon"

export function ResourceDetailsPage() {
  const resourceId = parseInt(useParams({ from: "/app/resources/$id" }).id)
  const navigate = useNavigate({ from: "/app/resources/$id" })
  const { colorScheme } = useMantineColorScheme()
  const { data: resource } = useGetResourceSuspense({ resourceId })
  const { data: objects } = useGetResourceObjectsByResourceIdSuspense({
    resourceId,
  })
  const { data: conditions = [] } = useGetConditionsByResourceId({ resourceId })
  const conditionMap = React.useMemo(
    () => new Map(conditions.map((c) => [c.resourceObjectConditionId, c.name])),
    [conditions]
  )
  const conditionIds = React.useMemo(
    () =>
      Array.from(
        new Set(
          objects
            .map((o) => o.resourceObjectConditionId)
            .filter((id) => id != null)
        )
      ),
    [objects]
  )
  const { data: objectFieldTypes } = useGetResourceFieldTypes(
    {
      category: resource.category,
      fieldVisibility: "ResourceObject",
      previewOnly: true,
    },
    { query: { initialData: [] } }
  )
  const { t } = useTranslation("app_server")

  const backgroundColor = React.useMemo(
    () =>
      colorScheme === "light"
        ? "var(--mantine-color-gray-3)"
        : "var(--mantine-color-dark-8)",
    [colorScheme]
  )

  const hoverBackgroundColor = React.useMemo(
    () =>
      colorScheme === "light"
        ? "var(--mantine-color-gray-4)"
        : "var(--mantine-color-dark-6)",
    [colorScheme]
  )

  const detailsData = React.useMemo(
    () => [
      { label: t("Name"), value: resource.name },
      { label: t("Type"), value: resource.resourceType },
      {
        label: t("RentalFee"),
        value: resource.rentalFee
          ? getFormattedCurrency(resource.rentalFee)
          : undefined,
      },
      {
        label: t("CleaningCost"),
        value: resource.cleaningCost
          ? getFormattedCurrency(resource.cleaningCost)
          : undefined,
      },
      { label: t("Location"), value: resource.location },
    ],
    [t, resource]
  )

  const imageAttachments = React.useMemo(
    () =>
      resource.attachments.filter((a) => getFileType(a.fileName) === "image"),
    [resource.attachments]
  )

  return (
    <Page>
      <ColumnLayout>
        {imageAttachments.length > 0 && (
          <DetailsCard title={t("Images")}>
            <AttachmentCarousel
              attachments={imageAttachments}
              containerId={resource.resourceId}
              controllerRoute="resources"
            />
          </DetailsCard>
        )}
        <DetailsCard title={t("Details")}>
          {detailsData
            .filter((d) => !!d.value)
            .map((data, i) => (
              <LabeledValue key={i} {...data} />
            ))}
        </DetailsCard>
        {resource.resourceFields.map((rf, i) => (
          <DetailsCard key={i} title={rf.key ?? t("Details")}>
            {rf.value?.map((rf) => (
              <LabeledValue
                key={rf.type.name}
                label={rf.type.name}
                value={<FieldData fieldData={rf} />}
              />
            ))}
          </DetailsCard>
        ))}
        <DetailsCard
          title={t(`ResourceObjectCategory_${resource.category}`)}
          buttons={
            <CreateResourceObjectsActionIcon
              resourceId={resourceId}
              category={resource.category}
            />
          }
        >
          <Box>
            <Box
              style={{
                display: "grid",
                gridTemplateColumns: `repeat(${2 + objectFieldTypes.length}, 1fr)`,
                gap: "1rem",
                marginBottom: "1rem",
              }}
            >
              <Text fw={500}>{t("InventoryNumber")}</Text>
              <Text fw={500}>{t("Condition")}</Text>
              {objectFieldTypes.map((t) => (
                <Text key={t.id} fw={500}>
                  {t.name}
                </Text>
              ))}
            </Box>
            {objects.map((o) => (
              <Box
                key={o.resourceObjectId}
                onClick={() =>
                  navigate({
                    to: "/app/resources/resource-objects/$id",
                    params: { id: o.resourceObjectId.toString() },
                  })
                }
                style={{
                  display: "grid",
                  gridTemplateColumns: `repeat(${2 + objectFieldTypes.length}, 1fr)`,
                  gap: "1rem",
                  padding: "1rem",
                  marginBottom: "0.5rem",
                  borderRadius: "4px",
                  backgroundColor,
                  cursor: "pointer",
                  transition: "background-color 150ms ease",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = hoverBackgroundColor)
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = backgroundColor)
                }
              >
                <Text>{o.identificationCode}</Text>
                <Text>
                  {o.resourceObjectConditionId != null
                    ? (conditionMap.get(o.resourceObjectConditionId) ?? "-")
                    : "-"}
                </Text>
                {objectFieldTypes.map((t) => {
                  const fieldData = o.objectFields.find(
                    (f) => f.type.name === t.name
                  )
                  return (
                    <Box key={t.id}>
                      {fieldData?.textValue ? (
                        <FieldData fieldData={fieldData} />
                      ) : (
                        <Text color="dimmed">-</Text>
                      )}
                    </Box>
                  )
                })}
              </Box>
            ))}
          </Box>
        </DetailsCard>
      </ColumnLayout>
    </Page>
  )
}
